import { CommandsContext } from 'context'
import { TFunction } from 'i18next'
import { noop } from 'lodash-es'
import { Proving, ProvingType, UserRole } from 'model'
import { ICommandBarItemProps } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthContext } from './useAuthContext'
import { SearchProps } from './useSearch'

export type Command = ICommandBarItemProps & {
  priority?: number
  farCommand?: boolean
  hidden?: boolean
}

export function useCommand(command: Command, deps?: any[]) {
  const { addCommand } = React.useContext(CommandsContext)

  return React.useEffect(() => {
    if (addCommand) {
      return addCommand(command)
    }

    return noop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [/** command, */ addCommand, ...(deps || [])])
}

interface ModelDescriptor {
  name: string
  count?: number
}
const modelCommandProps = {
  create: { iconName: 'Add', disabledFn: undefined },
  edit: { iconName: 'Edit', disabledFn: (count?: number) => count !== 1 },
  delete: {
    iconName: 'Delete',
    disabledFn: (count?: number) => !(count && count > 0),
  },
  show: { iconName: 'RedEye', disabledFn: (count?: number) => count !== 1 },
}

function useModelCommand(
  modelOrName: string | ModelDescriptor,
  operation: 'create' | 'edit' | 'delete' | 'show',
  command: Omit<Command, 'key'>,
  deps?: any[]
) {
  const { t } = useTranslation()

  const { name, count }: ModelDescriptor =
    typeof modelOrName === 'string' ? { name: modelOrName } : modelOrName
  const { iconName, disabledFn } = modelCommandProps[operation]

  return useCommand(
    {
      key: `${name}.${operation}`,
      iconProps: { iconName },
      disabled: disabledFn ? disabledFn(count) : false,
      text:
        count && count > 0 && operation === 'delete'
          ? t(`model.${name}.${operation}WithCount`, {
              count,
            })
          : t(`model.${name}.${operation}`),
      title: t(`model.${name}.${operation}`),
      ...command,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, ...(deps || [])]
  )
}

export const useCreateCommand = (
  modelName: string,
  command: Omit<Command, 'key'>,
  deps?: any[]
) => useModelCommand({ name: modelName }, 'create', command, deps)
export const useEditCommand = (
  model: string | ModelDescriptor,
  command: Omit<Command, 'key'>,
  deps?: any[]
) => useModelCommand(model, 'edit', command, deps)
export const useDeleteCommand = (
  model: string | ModelDescriptor,
  command: Omit<Command, 'key'>,
  deps?: any[]
) => useModelCommand(model, 'delete', command, deps)
export const useShowCommand = (
  model: string | ModelDescriptor,
  command: Omit<Command, 'key'>,
  deps?: any[]
) => useModelCommand(model, 'show', command, deps)

export const useDeselectCommand = (
  count: number,
  command: Omit<Command, 'key'>,
  deps?: any[]
) => {
  const { t } = useTranslation()

  return useCommand(
    {
      key: 'deselect',
      farCommand: true,
      hidden: count === 0,
      iconProps: { iconName: 'Cancel' },
      text: t('action.deselect', { count }),
      ...command,
    },
    deps
  )
}

export function useCustomSearchCommand(
  { value, ...props }: SearchProps,
  deps?: any[]
) {
  return useCommand(
    {
      key: 'search',
      priority: -999,
      data: {
        value: value || '',
        ...props,
      },
    },
    deps
  )
}

export function useSearchCommand(
  { onSearch, value, onClear, ...userProps }: SearchProps,
  deps?: any[]
) {
  const [search, setSearch] = React.useState(value)

  useCustomSearchCommand(
    {
      ...userProps,
      value: search,
      onSearch: (newValue) => {
        setSearch(newValue)
        if (onSearch) {
          onSearch(newValue)
        }
      },
      onClear: () => {
        setSearch('')
        if (onClear) {
          onClear()
        }
      },
    },
    [search, ...(deps || [])]
  )

  return search
}

export function useGroupCommand(
  t: TFunction,
  proving: Proving,
  selectedItems: any[],
  onAddGroupClick: () => void,
  onRemoveGroupClick: () => void,
  disabled: boolean,
  priority?: number,
  deps: any[] = []
) {
  return useCommand(
    {
      key: 'groups',
      text: t('model.proving.groups'),
      priority,
      hidden: !!(
        (proving.type ? proving.type !== ProvingType.Block : true) &&
        !proving.enableGroups
      ),
      disabled: !!disabled,
      iconProps: { iconName: 'GroupedList' },
      subMenuProps: {
        items: [
          {
            key: 'addGroup',
            text: t('model.proving.addGroup'),
            onClick: onAddGroupClick,
          },
          {
            key: 'removeGroup',
            text: t('model.proving.removeGroup'),
            onClick: onRemoveGroupClick,
          },
        ],
      },
    },
    [selectedItems, proving, ...deps]
  )
}

export function useCreateProvingCommand(priority: number, onClick: () => void) {
  const auth = useAuthContext()
  const canSeeProving =
    auth.hasUserRole(UserRole.ResourceManager) ||
    auth.hasUserRole(UserRole.SystemAdministrator) ||
    auth.hasUserRole(UserRole.ProvingCreator)
  return useCreateCommand('proving', {
    priority,
    hidden: !canSeeProving,
    onClick,
  })
}
